import { useState } from "react"

import { dialogClasses, Fab } from "@mui/material"
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'

import { formattedCurrency } from "@/utils"
import { PreviewContainer, PreviewBackground, PreviewContents, PrintPreviewContents, TopP, TopAddon, TopSpan, TopSheetSection, Image, Description, Signature, ExtraInfo, SenderNames, BidBills } from "@/sheet/TopSheetPreviewComponents"
import { store } from "@/sheet/bidStore"
import { topSheetColumns } from "./TopSheetColumns"

export const TopSheetPreview = ({printMode, viewOnly}) => {
    const bid = store.bid
    const project = store.bid.project
    const financialTerm = store.bid.financial_term
    const disclaimers = store.bid.disclaimers

    const getColumnSet = () => {
        let mergedColumns = []
        store.bid.line_groups.forEach(group=>{
            const applicableColumns = group.topsheet_columns.filter(column=>{
                const columnObj = topSheetColumns.find(c=>c.field == column)
                return !columnObj.ignoredByRateTypes.includes(group.rate_type)
            })
            mergedColumns = mergedColumns.concat(applicableColumns)
        })
        let columnSet = new Set(mergedColumns)
        columnSet = Array.from(columnSet)
        return columnSet
    }
    const columnSet = getColumnSet()

    const sections = store.bid.line_items_grouped
    const clientQuoted = store.bid.client_quoted
    const addons = store.bid.addons.filter(addon => addon.line_group_id == null)

    const [zoomLevel, setZoomLevel] = useState(0)
    
    const StyledContents = printMode ? PrintPreviewContents : PreviewContents

    if (!bid || !bid.line_items_grouped){
        return null
    }

    return (
        <PreviewContainer >
            {!printMode && (
                <>
                    <PreviewBackground zoomLevel={zoomLevel} />

                    <Fab
                        size="small"
                        sx={{position: "absolute", bottom: 64, right: 16}}
                        onClick={() => setZoomLevel(zoomLevel-1)}
                        disabled={(zoomLevel == 0)}
                    >
                        <ZoomOutIcon />
                    </Fab>
                    <Fab
                        size="small"
                        sx={{position: "absolute", bottom: 16, right: 16}}
                        onClick={() => setZoomLevel(zoomLevel+1)}
                        disabled={(zoomLevel == 2)}
                    >
                        <ZoomInIcon />
                    </Fab>
                </>
            )}

            <StyledContents zoomLevel={zoomLevel} condensed={!bid.top_sheet_condensed}>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" />
                <link href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;600&display=swap" rel="stylesheet"></link>

                {bid.status == "Drafts" ? 
                    <div style={{
                        margin: 0,
                        color: "black",
                        position: "absolute",
                        textAlign: "center",
                        pointerEvents: "none",
                    }}>
                        {
                            new Array(5).fill(0).map((_, index)=>{
                                return(
                                    <TopP key={index} size={12} style={{transform: "rotate(-30deg)", color: "#66666611", margin: 0, padding: 0}}>Draft</TopP>
                                )
                            })
                        }
                    </div> 
                : null}

                <div style={{display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: bid.top_sheet_condensed ? "1em" : "4em"}}>
                    <Image image={project.legal_entity.logo} size={2} />
                    <div style={{textAlign: "right", color: "#aaa"}}>
                        <TopP size={0.5} style={{whiteSpace: "pre-line"}}>{project.legal_entity.name}</TopP>
                        <TopP size={0.5} style={{whiteSpace: "pre-line"}}>{project.legal_entity.address}</TopP>
                    </div>
                </div>
            
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "start"}}>
                    <div>
                        <TopP size={1.2} style={{paddingBottom: bid.top_sheet_condensed ? "0.5em" : "1em"}}>{project.name}</TopP>
                        <div style={{color: "#aaa"}}>
                            <TopP size={0.6}>Project Estimate</TopP>
                            <SenderNames condensed={bid.top_sheet_condensed} />
                            <TopP size={0.6}>{ new Date().toDateString() }</TopP>
                            <TopP size={0.6}>{`#${ project.docket }`}</TopP>
                            <ExtraInfo />
                        </div>
                    </div>
                    <div style={{textAlign: "right"}}>
                        <TopP size={0.6} style={{fontWeight: 800, paddingTop: "1em", paddingBottom: "1em"}}>RECIPIENT</TopP>
                        <TopP size={0.6} >{project.client.name}</TopP>
                    </div>
                </div>
                <table>
                    <tbody>
                        {sections.map(section=>
                            <TopSheetSection
                                key={section.id}
                                id={section.id}
                                currency={project.currency}
                                showZero={bid.include_zero_lines}
                                showDecimal={bid.include_decimals}
                                columnSet={columnSet}
                                printMode={printMode}
                                viewOnly={viewOnly}
                                condensed={bid.top_sheet_condensed}
                            />
                        )}
                    </tbody>
                </table>
            
                <div style={{marginTop: bid.top_sheet_condensed ? "0.5em" : "2.5em"}}>
                    {Object.values(addons).map((addon, index)=>
                        <TopAddon 
                            key={index} 
                            name={addon.name} 
                            amount={addon.client_quoted} 
                            currency={project.currency} 
                            showZero={bid.include_zero_lines}
                            showDecimal={bid.include_decimals} 
                            condensed={bid.top_sheet_condensed}
                        />
                    )}
                </div>

                <div style={{marginBottom: bid.top_sheet_condensed ? "1em" : "2.5em", fontWeight: 600, marginLeft: "50%", display: "flex", justifyContent: "space-between", padding: bid.top_sheet_condensed ? "0" : "0.5em 0", borderBottom: "1px #ddd solid"}}>
                    <TopSpan size={0.7}>Grand Total</TopSpan>
                    <TopSpan size={0.7}>{formattedCurrency(clientQuoted, project.currency, false, bid.include_decimals ? 2 : 0)}</TopSpan>
                </div>
                
                <Description title="Project Notes and Assumptions" text={bid.assumptions} show={bid.include_notes && bid.assumptions} condensed={bid.top_sheet_condensed} />
                <Description title="Bid includes" text={bid.inclusions} show={bid.include_notes && bid.inclusions} condensed={bid.top_sheet_condensed} />
                <Description title="Bid does not include" text={bid.exclusions} show={bid.include_notes && bid.exclusions} condensed={bid.top_sheet_condensed} />

                <Description title="Financial Terms" text={financialTerm?.description} show={bid.include_financial_terms} condensed={bid.top_sheet_condensed} />
                    
                <BidBills show={bid.include_bill_schedule} projectCurrency={project.currency} />

                {disclaimers.map((disclaimer) =>
                    <Description 
                        key={disclaimer.id} 
                        title={disclaimer.name} 
                        text={disclaimer.description} 
                        show={bid.disclaimer_ids.includes(disclaimer.id)} 
                        condensed={bid.top_sheet_condensed} 
                    />
                )}
            
                <div style={{display: "flex", width: "100%" , justifyContent: "space-between", margin: bid.top_sheet_condensed ? "1em 0 2em 0" : "2em 0 3em 0", flexWrap: "wrap"}}>
                    {
                        Array(bid.signer_count).fill().map((_, i)=>
                            <Signature key={i} printMode={printMode}/>
                        )
                    }
                </div>
            </StyledContents>
        </PreviewContainer>
    )
}
